import React, { useCallback, useMemo } from 'react'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import { TreeItem } from '@mui/x-tree-view/TreeItem'
import { useQuery } from 'react-query'
import { apiJSON } from '@shared/utils/api/api.util'
import { Location } from '@shared/types'
import { ProgressButton } from '@shared/components/progress-button.component'

interface Props {
  fetchEnabled: boolean
  setSelectedIds: (ids: number[]) => void
  selectedIds: number[]
  isResourceEdit: boolean
  isPublished?: boolean
}

const companiesEndpoint = 'companies'

export const RequirementMappingCompaniesTree = ({
  fetchEnabled,
  setSelectedIds,
  selectedIds,
  isResourceEdit,
  isPublished,
}: Props) => {
  const { data: companies, isLoading: areCompaniesLoading } = useQuery<
    Location[]
  >(companiesEndpoint, () => apiJSON(companiesEndpoint), {
    enabled: fetchEnabled,
    staleTime: Infinity,
  })

  const handleSelectionToggle = useCallback(
    (event: React.SyntheticEvent, nodeId: string) => {
      setSelectedIds(
        selectedIds.includes(parseInt(nodeId))
          ? selectedIds.filter((id) => id !== parseInt(nodeId))
          : [...selectedIds, parseInt(nodeId)]
      )
    },
    [setSelectedIds, selectedIds]
  )

  const selectedItems = useMemo(
    () => selectedIds.map((id) => id.toString()),
    [selectedIds]
  )

  if (areCompaniesLoading || !companies) return null

  const filteredCompanies =
    isResourceEdit && isPublished
      ? companies.filter((company) => selectedIds.includes(company.companyID))
      : companies

  const allCompanyIds = filteredCompanies.map((c) => c.companyID)
  const isAnythingSelected = allCompanyIds.some((id) =>
    selectedIds.includes(id)
  )

  const toggleAll = () => {
    if (isAnythingSelected) {
      setSelectedIds(selectedIds.filter((id) => !allCompanyIds.includes(id)))
    } else {
      const newSelected = Array.from(
        new Set([...selectedIds, ...allCompanyIds])
      )
      setSelectedIds(newSelected)
    }
  }

  return (
    <>
      <ProgressButton
        color="primary"
        variant="contained"
        sx={{ marginBottom: 2 }}
        onClick={toggleAll}
        disabled={isPublished}
      >
        {isAnythingSelected ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
      </ProgressButton>
      <SimpleTreeView
        multiSelect
        checkboxSelection
        onItemSelectionToggle={
          isResourceEdit && isPublished ? undefined : handleSelectionToggle
        }
        selectedItems={selectedItems}
        disableSelection={isPublished}
      >
        {filteredCompanies.map((company) => (
          <TreeItem
            itemId={company.companyID.toString()}
            key={company.companyID}
            label={company.name}
          />
        ))}
      </SimpleTreeView>
    </>
  )
}
